/**
 * Interactive worksheet (form-fill) — used when activity_pdf.source === 'interactive'.
 *
 * Phase 1 (this file): render the PDF's real AcroForm fields with react-pdf so the
 * student types into them directly (NO Fabric pen/annotation — CTO decision #5),
 * capture the typed values by field name, and submit them as JSON.
 *
 * Auto-marking is server-side (Phase 2): the backend reads the embedded
 * `answer-key.json` from the worksheet PDF and grades the submitted answers, then
 * writes the score. This component does NOT mark client-side.
 *
 * No resubmission (CTO decision #7): once StudentAnsAttempts exist, the form is shown
 * read-only.
 *
 * Dispatched from worksheet.js; receives the already-fetched ActivityData + timer state.
 */
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { Container, Button, Modal, ModalBody, ModalFooter } from "reactstrap"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/esm/Page/AnnotationLayer.css"
import "react-pdf/dist/esm/Page/TextLayer.css"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Loader from "react-spinner-loader"
import { useNavigate } from "react-router-dom"

import { BASE_URL } from "../../global"
import { isInteractiveSource } from "../../modules/interactive-worksheet/worksheet-source"
import awicon from "../../assets/images/services-icon/coin.png"
import duration from "../../assets/images/services-icon/duration.png"

// react-pdf 7.7.x bundles pdfjs 3.11.174 — pin the worker to the SAME version.
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

// Build the worksheet PDF URL the same way worksheet.js does (activity_type 5 / 7).
const buildPdfUrl = ActivityData => {
  const node =
    ActivityData &&
    ActivityData.activity_node &&
    ActivityData.activity_node.length > 0
      ? ActivityData.activity_node[0]
      : null
  if (!node) return ""

  if (parseInt(ActivityData.activity_type) === 7) {
    if (parseInt(ActivityData.fk_sub_id) === 1) {
      return (
        "https://s3.ap-southeast-1.amazonaws.com/assets-dev.mygooroo.io/activity_pdf/english_activity_pdf/" +
        node.file_name
      )
    } else if (parseInt(ActivityData.fk_sub_id) === 2) {
      return (
        "https://s3.ap-southeast-1.amazonaws.com/assets-dev.mygooroo.io/activity_pdf/math_activity_pdf/" +
        node.file_name
      )
    }
  } else if (parseInt(ActivityData.activity_type) === 5) {
    return node.link ? node.link : ""
  }
  return ""
}

const InteractiveWorksheet = ({
  activityData,
  pkActivityKey,
  seconds,
  isRunning,
  formatTime,
  onBack,
}) => {
  const navigate = useNavigate()
  const authUser = JSON.parse(localStorage.getItem("authStudentUser")) || null

  const [pdfData, setPdfData] = useState(null) // Uint8Array of the worksheet PDF
  const [numPages, setNumPages] = useState(0)
  const [loader, setLoader] = useState(true)
  const [error, setError] = useState("")
  const [blocked, setBlocked] = useState(null) // failed answer key check
  const [submitting, setSubmitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const pdfProxyRef = useRef(null) // pdfjs document proxy (holds annotationStorage)

  const question = activityData?.LessonActivitiesQuestions?.[0] || null
  const alreadySubmitted = (question?.StudentAnsAttempts?.length || 0) > 0
  const node = activityData?.activity_node?.[0] || null

  // Check the answer key before showing anything. A worksheet whose key cannot be
  // read cannot be marked, so it is not opened at all: filling it in would score
  // zero. The check runs first so a blocked worksheet is never downloaded.
  useEffect(() => {
    let cancelled = false

    const check = async () => {
      if (!authUser) return { ok: false, code: "NO_AUTH" }
      axios.defaults.headers.common["Authorization"] = `Bearer ${authUser}`
      const response = await axios.post(
        BASE_URL + "studentAnswer/CheckInteractiveWorksheet",
        { pk_lesson_activity_key: activityData?.pk_lesson_activity_key }
      )
      return response.data || {}
    }

    const load = async () => {
      try {
        const verdict = await check()
        if (cancelled) return
        if (!verdict.ok) {
          console.error("Interactive worksheet blocked:", verdict.code, verdict.message)
          setBlocked(verdict)
          setLoader(false)
          return
        }
      } catch (err) {
        // A failed check is treated the same as a failed key: nothing is opened.
        console.error("Interactive worksheet check failed", err)
        if (cancelled) return
        setBlocked(err?.response?.data || { code: "CHECK_FAILED" })
        setLoader(false)
        return
      }

      const pdfUrl = buildPdfUrl(activityData)
      if (!pdfUrl) {
        setError("Worksheet PDF not found")
        setLoader(false)
        return
      }
      try {
        const res = await fetch(pdfUrl)
        const buf = new Uint8Array(await res.arrayBuffer())
        if (!cancelled) setPdfData(buf)
      } catch (err) {
        console.error("Failed to fetch interactive worksheet PDF", err)
        if (!cancelled) setError("Failed to load worksheet PDF")
      } finally {
        if (!cancelled) setLoader(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [activityData])

  const file = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData])

  const onDocumentLoad = useCallback(pdf => {
    pdfProxyRef.current = pdf
    setNumPages(pdf.numPages)
  }, [])

  // Read typed values keyed by field name from pdfjs annotationStorage.
  const readTypedValues = async () => {
    const pdf = pdfProxyRef.current
    if (!pdf) return {}
    const fieldObjects = (await pdf.getFieldObjects()) || {}
    const storage = pdf.annotationStorage.getAll() || {}
    const out = {}
    for (const [name, annots] of Object.entries(fieldObjects)) {
      let value = ""
      for (const a of annots) {
        const stored = storage[a.id]
        if (stored && stored.value !== undefined && stored.value !== "") {
          value = stored.value
          break
        }
        if (a.value) value = a.value
      }
      out[name] = value
    }
    return out
  }

  // Matches the legacy worksheet naming so both kinds of submission sit together
  // in the S3 student/submissions folder.
  const buildSubmissionFileName = () =>
    "worksheet_submission_" + Date.now() + Math.floor(Math.random() * 1000) + ".pdf"

  /**
   * The filled PDF for the tutor to look at.
   *
   * pdfjs writes an incremental update, which pdf-lib cannot reliably read back.
   * That is fine here because nothing reads this file programmatically: the score
   * comes from the typed values, and this copy is only ever opened by a person.
   */
  const saveFilledPdf = async () => {
    const pdf = pdfProxyRef.current
    if (!pdf || typeof pdf.saveDocument !== "function") return null
    try {
      const bytes = await pdf.saveDocument()
      return new Blob([bytes], { type: "application/pdf" })
    } catch (err) {
      console.error("Could not save the filled worksheet PDF", err)
      return null
    }
  }

  const handleSubmit = async () => {
    if (!authUser) {
      navigate("/login")
      return
    }
    setSubmitting(true)
    try {
      // Read the typed values BEFORE saving the document.
      const answers = await readTypedValues()
      const filledPdf = await saveFilledPdf()

      // The backend reads the embedded answer-key.json and marks server-side.
      // The filled PDF rides along as the tutor's review copy; marking does not
      // depend on it, so a missing one still submits.
      const formData = new FormData()
      if (filledPdf) {
        formData.append("pdfFile", filledPdf, buildSubmissionFileName())
      }
      formData.append("pk_lesson_activity_key", activityData.pk_lesson_activity_key)
      formData.append("fk_lesson_key", activityData.fk_lesson_key)
      formData.append("activity_type", activityData.activity_type)
      formData.append("fk_sub_id", activityData.fk_sub_id)
      formData.append("question_id", question?.fk_question_id)
      formData.append("fk_ses_key", activityData.fk_sest_key)
      formData.append("submitted_on", seconds)
      formData.append("answers", JSON.stringify(answers))

      axios.defaults.headers.common["Authorization"] = `Bearer ${authUser}`
      const response = await axios.post(
        BASE_URL + "studentAnswer/StudentAnswerInteractiveWorksheet",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      if (parseInt(response.data) === 1) {
        toast.success("Worksheet submitted", {
          autoClose: 1000,
          onClose: () => window.location.reload(),
        })
      } else {
        toast.error("Connection to server lost! Kindly inform admin", {
          autoClose: 3000,
        })
        setSubmitting(false)
      }
    } catch (err) {
      console.error("Error submitting interactive worksheet:", err)
      // 422 means the answer key stopped being readable between opening and
      // submitting. Nothing was saved, so the answers stay on screen.
      if (err?.response?.status === 422) {
        toast.error("This worksheet cannot be marked. Kindly inform your tutor", {
          autoClose: 5000,
        })
      } else {
        toast.error("Unable to submit worksheet.", { autoClose: 3000 })
      }
      setSubmitting(false)
    } finally {
      setConfirmOpen(false)
    }
  }

  return (
    <React.Fragment>
      <Loader show={loader} type="body" />
      <ToastContainer autoClose={3000} />

      {/* Submit confirmation (no resubmission once confirmed). */}
      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(!confirmOpen)}>
        <ModalBody>
          <p className="text-center fw-bold text-dark mb-0 font-size-18">
            Once submitted, you will not be able to change your answers!
          </p>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button className="btn-cst-danger-md" onClick={() => setConfirmOpen(false)}>
            CANCEL
          </Button>
          <Button className="btn-cst-success-md" disabled={submitting} onClick={handleSubmit}>
            CONFIRM
          </Button>
        </ModalFooter>
      </Modal>

      <div className="page-wrapper">
        <div
          style={{
            zIndex: 1000,
            position: "fixed",
            top: 0,
            width: "100%",
            transition: "top 0.3s ease-in-out",
          }}
        >
          <div className="cs-pg-header bg-dark-blue py-4">
            <Container className="custom-wt">
              <div className="d-flex align-items-center">
                <div className="mt-0 border-right me-4" style={{ width: "20%" }}>
                  <div
                    className="text-white font-size-14 text-uppercase cursor-pointer"
                    onClick={onBack}
                  >
                    <span>
                      <i className="fas fa-chevron-left me-1"></i>
                    </span>{" "}
                    Back
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between flex-grow-1">
                  <h6 className="mb-0 font-size-20 text-white w-60 fw-regular">
                    {node?.name || node?.node_name || "Worksheet"}
                  </h6>
                  <div className="d-flex align-items-center mb-1">
                    {!blocked && isRunning && parseInt(activityData?.tut_clock_is_active) !== 1 ? (
                      <div className="custom-timer2 d-flex align-items-center mt-0 me-3">
                        <img width={24} className="me-2" src={duration} alt="" />
                        <h1 className="text-end mb-0 mt-1" style={{ fontSize: "28px" }}>
                          {formatTime(seconds)}
                        </h1>
                      </div>
                    ) : null}
                    {blocked ? null : !alreadySubmitted ? (
                      <Button
                        className="btn btn-danger cs-btn-danger text-uppercase"
                        onClick={() => setConfirmOpen(true)}
                      >
                        SUBMIT
                      </Button>
                    ) : (
                      <span className="text-white font-size-14">Submitted</span>
                    )}
                  </div>
                </div>
              </div>
            </Container>
          </div>
        </div>

        <div className="assessement-section worksheet-width-hold" style={{ marginTop: "180px" }}>
          {blocked ? (
            <div className="text-center p-5">
              <p className="font-size-18 fw-bold text-dark mb-2">
                This worksheet cannot be opened
              </p>
              <p className="font-size-16 text-muted mb-0">Kindly inform your tutor</p>
            </div>
          ) : null}

          {error ? (
            <div className="text-danger font-size-16 p-4">{error}</div>
          ) : null}

          {file ? (
            <div className="d-flex flex-column align-items-center pb-100">
              <Document
                file={file}
                onLoadSuccess={onDocumentLoad}
                onLoadError={err => setError(err?.message || "Failed to load PDF")}
                loading="Loading worksheet…"
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <div key={i} className="mb-3 shadow-sm bg-white">
                    <Page
                      pageNumber={i + 1}
                      /* interactive form fields when not yet submitted; flat once submitted */
                      renderForms={!alreadySubmitted}
                      renderAnnotationLayer
                      renderTextLayer={false}
                      scale={1.4}
                    />
                  </div>
                ))}
              </Document>

              {/* "interactive" is the internal flag on activity_pdf.source, not a source to show. */}
              {node?.source && !isInteractiveSource(node.source) ? (
                <div className="mt-3">
                  <h4>{"Source -" + node.source}</h4>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </React.Fragment>
  )
}

InteractiveWorksheet.propTypes = {
  activityData: PropTypes.object,
  pkActivityKey: PropTypes.string,
  seconds: PropTypes.number,
  isRunning: PropTypes.bool,
  formatTime: PropTypes.func,
  onBack: PropTypes.func,
}

export default InteractiveWorksheet
